/**
 * LocalStorage Debugger Utility
 * 
 * This utility provides functions to debug localStorage issues,
 * particularly for the contact enquiries feature.
 */

/**
 * Check if localStorage is available and working
 * @returns {boolean} True if localStorage is available and working
 */
export const isLocalStorageAvailable = () => {
  try {
    const testKey = '__test_key__';
    localStorage.setItem(testKey, testKey);
    const result = localStorage.getItem(testKey) === testKey;
    localStorage.removeItem(testKey);
    return result;
  } catch (e) {
    return false;
  }
};

/**
 * Get all enquiries from localStorage
 * @returns {Array} Array of enquiries or empty array if none found
 */
export const getEnquiries = () => {
  try {
    const enquiries = localStorage.getItem('contact_enquiries');
    return enquiries ? JSON.parse(enquiries) : [];
  } catch (e) {
    console.error('Error parsing enquiries from localStorage:', e);
    return [];
  }
};

/**
 * Add a test enquiry to localStorage
 * @returns {Object} The added enquiry object
 */
export const addTestEnquiry = () => {
  try {
    const testEnquiry = {
      id: Date.now(),
      name: 'Test User',
      email: 'test@example.com',
      phone: '+66123456789',
      service: 'Test Service',
      message: 'This is a test enquiry added via the debugger',
      status: 'new',
      submittedAt: new Date().toISOString(),
      readAt: null
    };
    
    const currentEnquiries = getEnquiries();
    const updatedEnquiries = [testEnquiry, ...currentEnquiries];
    
    localStorage.setItem('contact_enquiries', JSON.stringify(updatedEnquiries));
    return testEnquiry;
  } catch (e) {
    console.error('Error adding test enquiry:', e);
    return null;
  }
};

/**
 * Clear all enquiries from localStorage
 * @returns {boolean} True if successful
 */
export const clearEnquiries = () => {
  try {
    localStorage.removeItem('contact_enquiries');
    return true;
  } catch (e) {
    console.error('Error clearing enquiries:', e);
    return false;
  }
};

/**
 * Fix common localStorage issues
 * @returns {Object} Result of the fix operation
 */
export const fixLocalStorage = () => {
  try {
    // 1. Check if localStorage is available
    if (!isLocalStorageAvailable()) {
      return {
        success: false,
        message: 'localStorage is not available in this browser'
      };
    }
    
    // 2. Check if contact_enquiries exists
    const enquiries = localStorage.getItem('contact_enquiries');
    
    // 3. If it doesn't exist, create it with an empty array
    if (!enquiries) {
      localStorage.setItem('contact_enquiries', JSON.stringify([]));
      return {
        success: true,
        message: 'Created empty contact_enquiries array in localStorage'
      };
    }
    
    // 4. If it exists but is invalid JSON, fix it
    try {
      JSON.parse(enquiries);
      return {
        success: true,
        message: 'contact_enquiries exists and is valid JSON'
      };
    } catch (e) {
      localStorage.setItem('contact_enquiries', JSON.stringify([]));
      return {
        success: true,
        message: 'Fixed invalid JSON in contact_enquiries'
      };
    }
  } catch (e) {
    return {
      success: false,
      message: `Error fixing localStorage: ${e.message}`
    };
  }
};

/**
 * Run a complete diagnostic on the localStorage and enquiry system
 * @returns {Object} Diagnostic results
 */
export const runDiagnostic = () => {
  const results = {
    localStorageAvailable: false,
    enquiriesExist: false,
    enquiriesValid: false,
    enquiriesCount: 0,
    newEnquiriesCount: 0,
    oldestEnquiryDate: null,
    newestEnquiryDate: null,
    browserInfo: navigator.userAgent,
    timestamp: new Date().toISOString(),
    errors: []
  };
  
  try {
    // Check localStorage availability
    results.localStorageAvailable = isLocalStorageAvailable();
    if (!results.localStorageAvailable) {
      results.errors.push('localStorage is not available');
      return results;
    }
    
    // Check if enquiries exist
    const enquiriesRaw = localStorage.getItem('contact_enquiries');
    results.enquiriesExist = !!enquiriesRaw;
    
    if (!results.enquiriesExist) {
      results.errors.push('contact_enquiries not found in localStorage');
      return results;
    }
    
    // Check if enquiries are valid JSON
    try {
      const enquiries = JSON.parse(enquiriesRaw);
      results.enquiriesValid = Array.isArray(enquiries);
      
      if (!results.enquiriesValid) {
        results.errors.push('contact_enquiries is not an array');
        return results;
      }
      
      // Count enquiries
      results.enquiriesCount = enquiries.length;
      
      if (results.enquiriesCount > 0) {
        // Count new enquiries
        results.newEnquiriesCount = enquiries.filter(e => e.status === 'new').length;
        
        // Get dates
        const dates = enquiries
          .map(e => new Date(e.submittedAt))
          .filter(d => !isNaN(d.getTime()))
          .sort((a, b) => a - b);
        
        if (dates.length > 0) {
          results.oldestEnquiryDate = dates[0].toISOString();
          results.newestEnquiryDate = dates[dates.length - 1].toISOString();
        }
      }
    } catch (e) {
      results.enquiriesValid = false;
      results.errors.push(`Invalid JSON: ${e.message}`);
    }
  } catch (e) {
    results.errors.push(`Diagnostic error: ${e.message}`);
  }
  
  return results;
};

// Export a debug function that can be called from the console
window.debugEnquiries = {
  isLocalStorageAvailable,
  getEnquiries,
  addTestEnquiry,
  clearEnquiries,
  fixLocalStorage,
  runDiagnostic
};

export default {
  isLocalStorageAvailable,
  getEnquiries,
  addTestEnquiry,
  clearEnquiries,
  fixLocalStorage,
  runDiagnostic
};
